import type { FC } from "react";
import { MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import type { EngagementRow, TopPost } from "../Types/ListeningReportTypes";
import { CardTitle, REPORT_KEY, ReportTag, SectionTitle, ViewPostLink, getBrandScope } from "./ReportPrimitives";

const ENGAGEMENT_KEY = `${REPORT_KEY}.engagement`;

interface EngagementSectionProps {
  rows: EngagementRow[];
  topPosts: TopPost[];
  primaryName: string;
  competitorName?: string;
}

const EngagementSection: FC<EngagementSectionProps> = ({ rows, topPosts, primaryName, competitorName }) => {
  const { t, i18n } = useTranslation();
  const format = (value: number | null) => (value === null ? "–" : formatNumber(value, {}, i18n.language));

  return (
    <section className="br__panel" id="br-engagement" data-pdf-block="br-engagement" data-pdf-splittable aria-label={t(`${REPORT_KEY}.tabs.engagement`, "Engagement")}>
      <SectionTitle scope={getBrandScope(t, primaryName, competitorName)}>
        {t(`${REPORT_KEY}.tabs.engagement`, "Engagement")}
      </SectionTitle>

      <div className="br__card br__card--flush">
        <div className="br__card-top">
          <div>
            <CardTitle help={t(`${ENGAGEMENT_KEY}.sourceHelp`, "Likes, comments and shares on collected posts")}>
              {t(`${ENGAGEMENT_KEY}.sourceTitle`, "Engagement by Source")}
            </CardTitle>
            <p className="br__card-desc">
              {t(`${ENGAGEMENT_KEY}.sourceDescription`, "Shows which conversations reached the most people.")}
            </p>
          </div>
        </div>
        <div className="br__table-wrap">
          <table className="br__table">
            <thead>
              <tr>
                <th>{t(`${ENGAGEMENT_KEY}.brand`, "Brand")}</th>
                <th>{t(`${ENGAGEMENT_KEY}.source`, "Source")}</th>
                <th className="br__num">{t(`${ENGAGEMENT_KEY}.posts`, "Posts")}</th>
                <th className="br__num">{t(`${ENGAGEMENT_KEY}.likes`, "Likes")}</th>
                <th className="br__num">{t(`${ENGAGEMENT_KEY}.comments`, "Comments")}</th>
                <th className="br__num">{t(`${ENGAGEMENT_KEY}.shares`, "Shares")}</th>
                <th className="br__num">{t(`${ENGAGEMENT_KEY}.average`, "Avg. engagement / post")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={`${row.brand}-${row.source}`}>
                  <td>{row.brand}</td>
                  <td>
                    <ReportTag small>{row.source}</ReportTag>
                  </td>
                  <td className="br__num">{format(row.posts)}</td>
                  <td className="br__num">{format(row.likes)}</td>
                  <td className="br__num">{format(row.comments)}</td>
                  <td className="br__num">{format(row.shares)}</td>
                  <td className="br__num">{format(row.averageEngagement)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="br__card br__card--flush">
        <div className="br__card-top">
          <div>
            <CardTitle help={t(`${ENGAGEMENT_KEY}.postsHelp`, "Posts with the most engagement, linked to the original")}>
              {t(`${ENGAGEMENT_KEY}.postsTitle`, "Top Performing Posts")}
            </CardTitle>
            <p className="br__card-desc">
              {t(
                `${ENGAGEMENT_KEY}.postsDescription`,
                "Open the original post to see the full context behind the comments."
              )}
            </p>
          </div>
        </div>
        <div className="br__table-wrap">
          <table className="br__table">
            <thead>
              <tr>
                <th>{t(`${ENGAGEMENT_KEY}.post`, "Post")}</th>
                <th className="br__num">{t(`${ENGAGEMENT_KEY}.likes`, "Likes")}</th>
                <th className="br__num">{t(`${ENGAGEMENT_KEY}.comments`, "Comments")}</th>
                <th className="br__num">{t(`${ENGAGEMENT_KEY}.shares`, "Shares")}</th>
                <th>{t(`${ENGAGEMENT_KEY}.sentiment`, "Sentiment")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {topPosts.map(post => (
                <tr key={`${post.brand}-${post.url ?? post.title}`}>
                  <td>
                    <div className="br__post">
                      <span className="br__sq">
                        <MessageSquare className="br-ic" aria-hidden />
                      </span>
                      <div>
                        <span>{post.title}</span>
                        <div className="br__post-tags">
                          <ReportTag small>{post.brand}</ReportTag>
                          <ReportTag small>{post.source}</ReportTag>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="br__num">{format(post.likes)}</td>
                  <td className="br__num">{format(post.comments)}</td>
                  <td className="br__num">{format(post.shares)}</td>
                  <td>
                    <ReportTag small tone={post.sentiment.tone}>
                      {post.sentiment.label}
                    </ReportTag>
                  </td>
                  <td>
                    <ViewPostLink url={post.url} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default EngagementSection;
